import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  limit,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface LoginLogInput {
  userId?: string;
  phone?: string;
  userName?: string;
  userRole?: string;
  success: boolean;
  failureReason?: string;
}

export interface LoginLog extends LoginLogInput {
  id: string;
  device: string;
  timestamp: Date;
}

const LOGS_COLLECTION = 'loginLogs';
const NOTIFS_COLLECTION = 'adminNotifications';
const FAILURE_THRESHOLD = 3;
const WINDOW_MINUTES = 30;

function getDevice(): string {
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    return navigator.userAgent;
  }
  return 'unknown';
}

/**
 * Écrit un log de connexion (succès ou échec) dans Firestore.
 * En cas d'échec, vérifie les tentatives récentes et crée une alerte si nécessaire.
 */
export async function writeLoginLog(input: LoginLogInput): Promise<void> {
  try {
    const payload = {
      userId: input.userId ?? null,
      phone: input.phone ?? null,
      userName: input.userName ?? null,
      userRole: input.userRole ?? null,
      success: input.success,
      failureReason: input.failureReason ?? null,
      device: getDevice(),
      timestamp: Timestamp.now(),
    };

    await addDoc(collection(db, LOGS_COLLECTION), payload);

    if (!input.success && (input.phone || input.userId)) {
      await checkConsecutiveFailures(input);
    }
  } catch (err) {
    console.error('writeLoginLog error:', err);
  }
}

/**
 * Vérifie s'il y a 3+ échecs consécutifs dans les 30 dernières minutes
 * pour le compte ciblé, et crée une notification admin si oui.
 */
async function checkConsecutiveFailures(input: LoginLogInput): Promise<void> {
  try {
    const since = new Date(Date.now() - WINDOW_MINUTES * 60 * 1000);

    const filterField = input.userId ? 'userId' : 'phone';
    const filterValue = input.userId ?? input.phone;
    if (!filterValue) return;

    const q = query(
      collection(db, LOGS_COLLECTION),
      where(filterField, '==', filterValue),
      where('timestamp', '>=', Timestamp.fromDate(since)),
      orderBy('timestamp', 'desc'),
      limit(10)
    );

    const snap = await getDocs(q);
    let consecutiveFailures = 0;
    for (const d of snap.docs) {
      const data = d.data();
      if (data.success === false) consecutiveFailures += 1;
      else break;
    }

    if (consecutiveFailures >= FAILURE_THRESHOLD) {
      await createSecurityAlert(input, consecutiveFailures);
    }
  } catch (err) {
    console.error('checkConsecutiveFailures error:', err);
  }
}

async function createSecurityAlert(
  input: LoginLogInput,
  failures: number
): Promise<void> {
  try {
    await addDoc(collection(db, NOTIFS_COLLECTION), {
      type: 'security_alert',
      title: 'Tentatives de connexion suspectes',
      message: `${failures} tentatives de connexion échouées en moins de ${WINDOW_MINUTES} minutes pour ${
        input.userName ?? input.phone ?? 'un compte'
      }.`,
      userId: input.userId ?? null,
      phone: input.phone ?? null,
      userName: input.userName ?? null,
      userRole: input.userRole ?? null,
      device: getDevice(),
      isRead: false,
      createdAt: Timestamp.now(),
    });
  } catch (err) {
    console.error('createSecurityAlert error:', err);
  }
}

/**
 * Récupère tous les logs de connexion (les plus récents en premier).
 */
export async function fetchLoginLogs(max = 500): Promise<LoginLog[]> {
  const q = query(
    collection(db, LOGS_COLLECTION),
    orderBy('timestamp', 'desc'),
    limit(max)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data() as any;
    return {
      id: d.id,
      userId: data.userId ?? undefined,
      phone: data.phone ?? undefined,
      userName: data.userName ?? undefined,
      userRole: data.userRole ?? undefined,
      success: !!data.success,
      failureReason: data.failureReason ?? undefined,
      device: data.device ?? 'unknown',
      timestamp: data.timestamp?.toDate?.() ?? new Date(),
    };
  });
}
